import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { useArchiveMutations, useArchivedFiles } from '../api/admin'
import { fileUrl, useCompanies, useReports } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, GradeBadge, Tabs, fmtAuditPeriod, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

export default function ArchivePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  const tab = params.get('tab') ?? 'reports'
  const filters = { page: Number(params.get('page') ?? 1), search: params.get('search') ?? '', companyId: params.get('companyId') ?? '', kind: params.get('kind') ?? '' }
  const reports = useReports({ page: filters.page, search: filters.search, companyId: filters.companyId, status: 'Archived', pageSize: 25, sort: '-updatedAt' })
  const files = useArchivedFiles(filters)
  const companies = useCompanies()
  const m = useArchiveMutations()
  const [error, setError] = useState<string | null>(null)
  const canEdit = user?.roles.some((r) => ['TenantAdmin', 'SuperAdmin', 'Auditor'].includes(r.role)) ?? false
  const page = tab === 'reports' ? reports.data : files.data

  return (
    <div className="stack">
      <h2>{t('archive.title')}</h2>
      <p className="muted small">{t('archive.hint')}</p>
      <Tabs active={tab} onChange={(k) => set('tab', k)} tabs={[{ key: 'reports', label: t('archive.reports') }, { key: 'files', label: t('archive.files') }]} />
      <div className="card filters">
        <input placeholder={t('common.search')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.companyId} onChange={(e) => set('companyId', e.target.value)}>
          <option value="">{t('common.allCompanies')}</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {tab === 'files' ? (
          <select value={filters.kind} onChange={(e) => set('kind', e.target.value)}>
            <option value="">{t('archive.allKinds')}</option>
            {['ImportSource', 'Document', 'TemplateExcel'].map((k) => <option key={k} value={k}>{t(`attachment.${k}`, k)}</option>)}
          </select>
        ) : null}
      </div>
      {error ? <p className="error">{error}</p> : null}
      <div className="card">
        {tab === 'reports' ? (
          reports.isLoading ? <p>{t('common.loading')}</p> : !reports.data?.items.length ? <Empty text={t('archive.emptyReports')} /> : (
            <div className="table-wrap"><table className="table">
              <thead><tr><th>{t('reports.code')}</th><th>{t('reports.title')}</th><th>{t('companies.name')}</th><th>{t('reports.date')}</th><th>{t('reports.result')}</th><th className="num">{t('reports.findings')}</th><th /></tr></thead>
              <tbody>{reports.data.items.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/reports/${r.id}`}><b>{r.code ?? r.templateCode}</b></Link><div className="muted small">{r.templateCode}</div></td>
                  <td>{r.title}</td>
                  <td className="small">{r.companyName}{r.siteName ? ` · ${r.siteName}` : ''}</td>
                  <td>{fmtAuditPeriod(r.auditStartDate, r.auditStartTime, r.auditEndDate, r.auditEndTime)}</td>
                  <td><GradeBadge grade={r.overallGrade} percent={r.overallPercent} /></td>
                  <td className="num">{r.findingCount}</td>
                  <td>{canEdit ? <button className="ghost" onClick={() => { setError(null); m.unarchive.mutateAsync(r.id).catch((e: unknown) => setError(errMsg(e))) }}>{t('archive.unarchive')}</button> : null}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )
        ) : (
          files.isLoading ? <p>{t('common.loading')}</p> : !files.data?.items.length ? <Empty text={t('archive.emptyFiles')} /> : (
            <div className="table-wrap"><table className="table">
              <thead><tr><th>{t('archive.fileName')}</th><th>{t('archive.kind')}</th><th>{t('reports.code')}</th><th>{t('companies.name')}</th><th>{t('reports.date')}</th><th /></tr></thead>
              <tbody>{files.data.items.map((f) => (
                <tr key={f.attachmentId}>
                  <td>{f.fileName}<div className="muted small">{(f.sizeBytes / 1024).toFixed(0)} KB · {f.contentType}</div></td>
                  <td><Badge>{t(`attachment.${f.kind}`, f.kind)}</Badge></td>
                  <td><Link to={`/reports/${f.formInstanceId}`}>{f.reportCode ?? f.reportTitle ?? t('reports.open')}</Link></td>
                  <td className="small">{f.companyName}</td>
                  <td>{fmtDateTime(f.createdAt)}</td>
                  <td><button className="ghost" onClick={() => void fileUrl(f.fileId).then((u) => window.open(u, '_blank'))}>{t('archive.download')}</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )
        )}
        {page && page.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 8 }}>
            <button className="secondary small-btn" disabled={filters.page <= 1} onClick={() => set('page', String(filters.page - 1))}>‹</button>
            <span className="muted small">{t('common.pageOf', { page: page.page, pages: page.totalPages, total: page.totalCount })}</span>
            <button className="secondary small-btn" disabled={filters.page >= page.totalPages} onClick={() => set('page', String(filters.page + 1))}>›</button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
